/* eslint-disable */
import React, { useState, useMemo, useEffect, useRef } from "react";
import { Tooltip } from "@mui/material";
import dayjs from "dayjs";
import FilterBar from "../../lib/components/Filterbar/Filterbar";
import MessageModal from "../../lib/components/MessageModal/MessageModal";
import ChargerDisplay from "../../lib/components/ChargerDisplay";
import MessageCell from "./MessageCell";
import "./styles.scss";
import Pagination from "../../lib/components/Pagination/Pagination";
import FeatureChip from "./FeatureChip";
import { fetchOcppMessages } from "./hooks/api";
import SlideOutPanel from "./SlideOutPanel";
import DataTable from "./DataTable";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider";
import { MESSAGE_CONFIG } from "../../lib/components/Filterbar/constants";
import { getLocalStorage, patchLocalStorage, LOCAL_STORAGE_KEYS } from "../../lib/utils/localStorageUtil";
import { renderMsgType } from "./utility";

export default function OcppMessages() {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPayload, setModalPayload] = useState(null);
  const [modalIndex, setModalIndex] = useState(null);
  const { serialNumber: globalSerialNumber, dateRange: globalDateRange, siteId } = useGlobalFilters();
  const [serialNumber, setSerialNumber] = useState(globalSerialNumber);
  const [dateRange, setDateRange] = useState(globalDateRange);
  const [order, setOrder] = useState("asc"); // 'asc' | 'desc'
  const [apiData, setApiData] = useState({});
  const [page, setPage] = useState(0); //state for current page
  const [rowsPerPage, setRowsPerPage] = useState(25); // State for rows per page
  const [openChargerDetailsSlideout, setOpenChargerDetailsSlideout] = useState(false);
  const [filters, setFilters] = useState({
    msgType: "",
    feature: [],
    search: "",
    src: "",
  });
  const advancedFiltersLoaded = useRef(false);
  const [showLoader, setShowLoader] = useState(true);
  const isInitialMount = useRef(true);

  const paginatedData = useMemo(() => {
    return apiData?.messages || [];
  }, [apiData]);

  useEffect(() => {
    setSerialNumber(globalSerialNumber);
  }, [globalSerialNumber]);

  // Load persisted advanced filters once on mount
  useEffect(() => {
    if (advancedFiltersLoaded.current) return;
    advancedFiltersLoaded.current = true;

    try {
      const savedFilters = getLocalStorage(LOCAL_STORAGE_KEYS.FILTERBAR_STATE) || {};

      setFilters(prev => ({
        ...prev,
        msgType: savedFilters.msgType || "",
        feature: Array.isArray(savedFilters.feature) ? savedFilters.feature : [],
        search: savedFilters.search || "",
      }));
    } catch (err) {
      // ignore storage errors for filters
    }
  }, []);

  // Persist advanced filters whenever they change
  useEffect(() => {
    if (!advancedFiltersLoaded.current) return; // unchanged logic

    try {
      patchLocalStorage(LOCAL_STORAGE_KEYS.FILTERBAR_STATE, {
        msgType: filters.msgType || "",
        feature: Array.isArray(filters.feature) ? filters.feature : [],
        search: filters.search || "",
      });
    } catch (err) {
      // swallow storage errors
    }
  }, [filters.msgType, filters.feature, filters.search]);

  const totalPages = Math.ceil(apiData.totalCount / rowsPerPage);

  //        {
  //           "ts": "2025-12-10T19:26:35.338Z",
  //           "msgType": 3,
  //           "msgId": "d03d0153-d038-484a-884c-335d0f3dd47b",
  //           "feature": "MeterValues",
  //           "status": 3,
  //           "msg": "[3,\"d03d0153-d038-484a-884c-335d0f3dd47b\",{}]",
  //           "src": "Cloud"
  //       },

  //   public enum OcppMessageType {
  //     CALL(2),
  //     CALLRESULT(3),
  //     CALLERROR(4);
  // }

  const handleSortToggle = () => {
    setOrder(prev => (prev === "asc" ? "desc" : "asc"));

    setFilters(prev => {
      if (order === "asc") {
        return { ...prev, sortBy: "ts" };
      } else {
        const { sortBy, ...rest } = prev;
        return rest;
      }
    });
  };

  const cleanFilterObject = filters => {
    const cleaned = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (!value) return; // skip empty fields

      if (key === "msgType" && MESSAGE_CONFIG[value]) {
        const mapped = MESSAGE_CONFIG[value];

        cleaned.src = mapped.source;
        cleaned.msgType = mapped.msgType;
      } else if (key === "feature") {
        if (Array.isArray(value) && value.length > 0) {
          cleaned[key] = value;
        }
        return;
      } else {
        cleaned[key] = value;
      }
    });

    return cleaned;
  };
  useEffect(() => {
    if (serialNumber) {
      fetchMessages(serialNumber, dateRange, page + 1, rowsPerPage, filters);
    } else {
      setShowLoader(false);
    }
  }, [serialNumber, dateRange, page, rowsPerPage, filters, order]);

  async function fetchMessages(serialNumber, dateRange, page, rowsPerPage, filters) {
    setShowLoader(true);
    try {
      const res = await fetchOcppMessages(
        serialNumber,
        new Date(dateRange[0]).toISOString(),
        new Date(dateRange[1]).toISOString(),
        page,
        rowsPerPage,
        cleanFilterObject(filters)
      );
      setApiData({
        ...res,
        messages: res?.messages ? [...res.messages] : [],
      });
    } catch (e) {
      setApiData({});
    }
    setShowLoader(false);
  }

  const openModal = (payload, idx) => {
    setModalPayload(payload);
    setModalIndex(idx);
    setModalOpen(true);
  };

  // Key navigation in modal (up/down arrows)
  useEffect(() => {
    if (!modalOpen) return;
    const handleKeyDown = e => {
      if (e.key === "ArrowUp" && modalIndex > 0) {
        const prev = apiData && apiData.messages[modalIndex - 1];
        if (prev) {
          setModalPayload(prev);
          setModalIndex(modalIndex - 1);
        }
      } else if (e.key === "ArrowDown" && modalIndex < apiData.messages.length - 1) {
        const next = apiData && apiData.messages[modalIndex + 1];
        if (next) {
          setModalPayload(next);
          setModalIndex(modalIndex + 1);
        }
      } else if (e.key === "Escape") {
        setModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, modalIndex, apiData.messages]);

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = newRowsPerPage => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to the first page when rows per page changes
  };

  const handleDateChange = range => {
    setDateRange(range);
  };

  const ocpp_message_columns = [
    {
      key: "timestamp",
      label: "Timestamp",
      width: "200px",
      sortable: true,
      render: row => dayjs(row.ts).format("YYYY-MM-DD HH:mm:ss.SSS"),
    },
    {
      key: "type",
      label: "Type",
      width: "65px",
      render: row => renderMsgType(row.msgType, row.src),
    },
    {
      key: "feature",
      label: "Feature",
      width: "215px",
      render: row => <FeatureChip feature={row.feature} />,
    },
    {
      key: "message",
      label: "Message",
      render: row => <MessageCell payload={row.msg} />,
    },
  ];

  return (
    <div className="ocpp-page" style={{ display: "flex", minHeight: "100vh", alignItems: "stretch" }}>
      <div style={{ flex: 1, padding: 32 }}>
        <h1 className="text-2xl mb-2 font-bold">OCPP Messages</h1>
        <div>
          <FilterBar
            dateRangeData={dateRange}
            onSearch={setSerialNumber}
            onDateChange={handleDateChange}
            isRefreshShown={true}
            handleRefresh={() => fetchMessages(serialNumber, dateRange, page + 1, rowsPerPage, filters)}
            openChargerDetailsSlideout={() => setOpenChargerDetailsSlideout(true)}
            chargerDetails={true}
            filters={filters}
            onMessageTypeChange={value => {
              setFilters(prev => ({ ...prev, msgType: value }));
            }}
            onFeatureChange={value => {
              setFilters(prev => ({ ...prev, feature: value }));
            }}
            onDetailSearch={value => {
              setFilters(prev => ({ ...prev, search: value }));
            }}
            isAdvancedFiltersShown={true}
          />
        </div>
        <ChargerDisplay serialNumber={serialNumber} onClick={() => setOpenChargerDetailsSlideout(true)} />
        <DataTable
          columns={ocpp_message_columns}
          rows={paginatedData}
          isLoading={showLoader}
          onRowClick={openModal}
          highlightedRowIndex={modalOpen ? modalIndex : null}
          sortConfig={{ key: "timestamp", order }}
          onSortToggle={handleSortToggle}
        />
        <Pagination
          totalPages={totalPages}
          currentPage={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalItems={apiData.totalCount}
        />

        <MessageModal open={modalOpen} messages={modalPayload} onClose={() => setModalOpen(false)} />

        {modalOpen && (
          <div
            style={{
              position: "fixed",
              bottom: 32,
              left: 0,
              right: 0,
              textAlign: "center",
              pointerEvents: "none",
              zIndex: 1301,
            }}
          >
            <span
              style={{
                background: "#1f2937",
                padding: "8px 20px",
                borderRadius: 8,
                fontSize: 14,
                color: "#f9fafb",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              }}
            >
              Use ↑ / ↓ arrows to navigate messages, Esc to close
            </span>
          </div>
        )}
        {openChargerDetailsSlideout && (
          <SlideOutPanel open={openChargerDetailsSlideout} onClose={() => setOpenChargerDetailsSlideout(false)} />
        )}
      </div>
    </div>
  );
}
